import { Alert, Button, Spin, Tag } from "antd";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileText,
  RefreshCw,
  Shield,
  Upload,
  XCircle,
} from "lucide-react";
import { useState } from "react";

import type { BusinessEntityDetailVO } from "@/api/entities/type";
import type { OssFileUploadVO } from "@/api/file/type";
import { SectionTitle } from "@/components/DetailBlocks";
import { UploadFile } from "@/components/UploadFile";
import { resolveOssAccessUrl } from "@/components/UploadFile/utils";
import { useOssSignedUrl } from "@/hooks/useOssSignedUrl";

/** 营业执照单文件大小上限：10MB */
const LICENSE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/** LicenseTab 入参 */
export interface LicenseTabProps {
  detail: BusinessEntityDetailVO | null;
  /** 有编辑权限时可直接上传/替换/清除（对齐原型，无需先点编辑） */
  canEdit?: boolean;
  /** 落库中 */
  saving?: boolean;
  /** 上传或移除后立即持久化；空字符串表示清除 */
  onLicensePersist?: (url: string) => void;
}

/** 证照管理 Tab：营业执照状态与照片；有文件时换签后预览图片 */
export const LicenseTab = ({
  detail,
  canEdit = false,
  saving = false,
  onLicensePersist,
}: LicenseTabProps) => {
  /** 重新上传态：展示拖拽区替换现有证照 */
  const [reuploading, setReuploading] = useState(false);
  const licenseFileUrl = detail?.licenseFileUrl ?? "";
  const hasLicense = !!licenseFileUrl;
  /** 有证照时换取签名 URL，供 img 内联预览（私有 Bucket 不可直链） */
  const { signedUrl: licensePreviewUrl, loading: licensePreviewLoading } = useOssSignedUrl({
    fileUrl: licenseFileUrl || undefined,
    enabled: hasLicense && !reuploading,
  });

  /** 上传完成：落库并退出重新上传态 */
  const handleLicenseUploadChange = (value?: OssFileUploadVO | OssFileUploadVO[]) => {
    const file = Array.isArray(value) ? value[0] : value;
    const nextUrl = file?.url ?? "";
    if (!nextUrl || nextUrl === licenseFileUrl) return;
    onLicensePersist?.(nextUrl);
    setReuploading(false);
  };

  /** 清除已上传证照 */
  const handleRemoveLicense = () => {
    onLicensePersist?.("");
    setReuploading(false);
  };

  /** 新窗口打开原图（重新换签，避免链接过期） */
  const handleOpenOriginalImage = async () => {
    if (!licenseFileUrl) return;
    const accessUrl = await resolveOssAccessUrl(licenseFileUrl, "inline");
    window.open(accessUrl, "_blank", "noopener,noreferrer");
  };

  /** 是否展示上传拖拽区：无证照可编，或主动重新上传 */
  const showUploader = canEdit && (!hasLicense || reuploading);

  return (
    <div className="space-y-5">
      <div>
        <SectionTitle icon={Shield} title="营业执照信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <Shield className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-500 w-24 shrink-0">证照状态</span>
            {detail?.licenseStatus ? (
              <Tag bordered className="text-[10px] m-0">
                {detail.licenseStatus}
              </Tag>
            ) : (
              "—"
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <Upload className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-500 w-24 shrink-0">副本上传</span>
            {hasLicense ? (
              <Tag color="success" className="text-[10px] m-0 border-0">
                已上传
              </Tag>
            ) : (
              <Tag bordered className="text-[10px] m-0 text-slate-400">
                未上传
              </Tag>
            )}
          </div>
        </div>
      </div>
      <div>
        <SectionTitle icon={FileText} title="营业执照照片" />
        <Spin spinning={saving}>
          {hasLicense && !reuploading ? (
            <div className="border rounded-lg overflow-hidden bg-slate-50/50">
              <div className="p-4 flex justify-center min-h-32">
                <Spin spinning={licensePreviewLoading}>
                  {licensePreviewUrl ? (
                    <img
                      src={licensePreviewUrl}
                      alt="营业执照"
                      className="max-w-md max-h-64 object-contain rounded border border-slate-200 bg-white"
                    />
                  ) : (
                    !licensePreviewLoading && (
                      <span className="text-sm text-slate-400">营业执照预览加载失败</span>
                    )
                  )}
                </Spin>
              </div>
              <div className="p-3 flex items-center justify-between bg-white/80 border-t gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span>已上传 · 营业执照副本</span>
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <>
                      <Button
                        type="link"
                        size="small"
                        className="h-6 text-xs p-0"
                        disabled={saving}
                        onClick={() => setReuploading(true)}
                        icon={<RefreshCw className="w-3 h-3" />}
                      >
                        重新上传
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        danger
                        className="h-6 text-xs p-0"
                        disabled={saving}
                        onClick={handleRemoveLicense}
                        icon={<XCircle className="w-3 h-3" />}
                      >
                        删除
                      </Button>
                    </>
                  )}
                  <Button
                    type="link"
                    size="small"
                    className="h-6 text-xs p-0"
                    disabled={!licenseFileUrl}
                    onClick={() => void handleOpenOriginalImage()}
                    icon={<ExternalLink className="w-3 h-3" />}
                  >
                    查看原图
                  </Button>
                </div>
              </div>
            </div>
          ) : showUploader ? (
            <div className="space-y-2">
              {reuploading && (
                <div className="flex justify-end">
                  <Button
                    type="link"
                    size="small"
                    className="h-6 text-xs p-0"
                    onClick={() => setReuploading(false)}
                  >
                    取消重新上传
                  </Button>
                </div>
              )}
              <UploadFile
                onChange={handleLicenseUploadChange}
                maxCount={1}
                alwaysShowDropZone
                variant="compact"
                ossType={1}
                accept="image/*"
                maxSizeBytes={LICENSE_MAX_SIZE_BYTES}
                dragHint="点击或拖拽上传营业执照"
                formatHint="支持 JPG/PNG 格式，最大 10MB"
                disabled={saving}
              />
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50">
              <Upload className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm m-0">暂未上传营业执照照片</p>
              <p className="text-xs mt-1 m-0">支持 JPG/PNG 格式，最大 10MB</p>
            </div>
          )}
        </Spin>
      </div>
      {detail?.licenseExpiringSoon && (
        <Alert
          type="warning"
          showIcon
          icon={<AlertCircle className="w-4 h-4" />}
          message="请确保营业执照在有效期内，过期需及时更新"
        />
      )}
    </div>
  );
};
